import { useCallback, createElement, useRef } from "react";
import { evaluateCollision } from "@shared/modules/journeys/collisionGeometry";
import { useAuth } from "../../../context/AuthProvider";
import { useModal } from "../../../context/ModalProvider";
import {
  createJourney,
  createJourneyOrMilestone,
  goalArchiveTuneupsFromError,
  isMilestoneResult,
  journeyOverlapConflictFromError,
  journeyRaceName,
  notifyJourneyChanged,
  patchJourney,
  type CreatedJourneyRow,
  type CreateJourneyBody,
  type CreateJourneyResult,
  type CreateMilestoneResult,
  type EventRef,
  type JourneyOverlapConflict,
  type JourneyView,
  type OverlapMode,
  type RoleOutcome
} from "../../../lib/journeyApi";
import { deleteEvent, type EventDTO } from "../../../lib/eventsApi";
import { userFacingError } from "../../../lib/api";
import { addAnonPendingRaceEvent } from "../../../lib/anonDraft";
import { patchFuelingProfile } from "../../../lib/fuelingProfileApi";
import {
  computeRacePlanImpact,
  defaultJourneyWindow,
  journeyCoveringDate,
  type RacePlanImpact
} from "../../../lib/journeyWindow";
import { compareYmd, localTodayYmd } from "../../../lib/dates";
import type { EventIntent } from "../IntentChoiceCards";
import {
  RoleResolutionSheet,
  type RoleResolutionChoice,
  type RoleResolutionContext
} from "../RoleResolutionSheet";
import {
  RacePlanConfirmModal,
  type RacePlanWindowChoice
} from "../RacePlanWindowConfirm";
import { EventDateEditModal } from "../EventDateEditModal";
import type { WizardApplyBody } from "../TrainingPlanWizard";

/**
 * `useEventSetup` — THE shared event→journey funnel (journeys overhaul P2,
 * rules J4/D2/D6). Every entry point that turns a picked race into fueling
 * routes through here: the planner/MyTraining add-event flow,
 * PersonalizeAndEvents, the wizard apply (via `useWizardFinish`, every
 * wizard host), and the wizard's milestone
 * short-circuit. One funnel, not N copies:
 *
 *   pick → (role-resolution sheet, ONLY when the date collides with an active
 *   journey's window) → event create (customer) → journey create with a
 *   D2-defaulted window → 409 overlap → the SAME role-resolution sheet →
 *   retry with `overlapResolution` / `resolveAsMilestone`.
 *
 * Anon principals (customer-event-anon-ownable): a guest's race is a REAL
 * `CustomerEvent` scoped to the anon session whenever the journey create runs —
 * `POST /api/journeys` creates it (build-with-race) or `applyTrainingPlan`
 * does (`fuel_goods_plan`), and the login merge re-points it onto the account.
 * Those paths write NO localStorage draft. The pending-race draft
 * (`addAnonPendingRaceEvent`) survives only on the paths that still reach no
 * server event for a guest: a tune-up/milestone (no journey create at all) and
 * the "just save the event" exit (the funnel's `createEvent` is customer-only).
 * `anonRaceReplay` keeps consuming those, plus any draft written by an older
 * build.
 *
 * Role-conflict unification (spec §9.1, C2): the funnel used to open two
 * bespoke modals — `IntentChoiceModal` (goal-vs-tune-up) and
 * `OverlapResolutionSheet` (chain/split/demote). Both are replaced by the ONE
 * `RoleResolutionSheet`, driven by the shared collision geometry
 * (`@shared/modules/journeys/collisionGeometry`) so the client pre-check and the
 * server 409 gate can never disagree on which outcomes are valid. The critical
 * new capability vs the old sheet: the **earlier-race → `nest_new`** outcome
 * (mark the newly-added race a tune-up, create no journey) now works, closing
 * the direction-blind-demote orphan (spec §7.1).
 *
 * Server-readiness: `createJourneyBodySchema` and the events zod bodies are
 * plain `z.object`s (unknown keys stripped), so the new fields (`startYmd`,
 * `journeyType`, `overlapResolution`, `resolveAsMilestone`, `intent`) are safe
 * to send before the server lane lands — they simply no-op until then.
 */

/** A race resolved by an entry point (event row may or may not exist yet). */
export type FunnelRace = {
  name: string;
  /** `YYYY-MM-DD` (race day). */
  dateYmd: string;
  /** Set when a CustomerEvent row already exists for this race. */
  customerEventId?: string;
  eventCatalogId?: string;
  distanceLabel?: string;
};

export type EventSetupOutcome =
  /** `journey` is the create response's RAW row (`CreatedJourneyRow`) — for
   *  the derived view, refetch (`notifyJourneyChanged()` already fired). */
  | { outcome: "goal"; journey: CreatedJourneyRow; event: EventDTO | null }
  | { outcome: "milestone"; event: EventDTO | null }
  /** Event saved (or kept) but no journey — user skipped/cancelled the plan. */
  | { outcome: "event_only"; event: EventDTO | null }
  /**
   * The user opted into the heavy wizard from the window sheet (C1): the event
   * (customer) is already created, but NO journey — the wizard owns plan setup
   * from here. The caller's `onBuildPlan` has already been invoked with the race
   * (carrying any created `customerEventId`) so the wizard can preload it.
   */
  | { outcome: "build_plan"; event: EventDTO | null }
  | { outcome: "cancelled" };

export type EventSetupInput = {
  race: FunnelRace;
  /**
   * The caller's active journeys (drives the J4 collision pre-check). Callers
   * already hold these (`useJourneys`); the funnel never re-fetches.
   */
  journeys: readonly JourneyView[];
  /**
   * Creates the CustomerEvent with the resolved intent — supplied by customer
   * entry points whose event does NOT yet exist (the funnel resolves intent
   * BEFORE creating, per J4). Omit when the event already exists, or for a
   * guest: the journey create makes the anon-owned event instead, and the
   * no-journey exits fall back to the pending draft.
   */
  createEvent?: (intent: EventIntent) => Promise<EventDTO>;
  /**
   * Confirmed window + cadence. Omit to run the funnel's own confirm sheet
   * (`askWindow: true`, planner add-event) or to default silently
   * (`askWindow: false`, e.g. a caller that already showed its own preview).
   */
  window?: RacePlanWindowChoice;
  /** Show the mini window/cadence confirm sheet when no `window` was given. */
  askWindow?: boolean;
  /** Library plan lengths for the distance when the caller has them (D2). */
  libraryLengths?: number[] | null;
  /** Force the intent (skips the sheet) — wizard short-circuit paths. */
  forcedIntent?: EventIntent;
  /**
   * Opt-in to the heavy wizard from the window sheet (C1). When provided, the
   * window sheet grows a third "Build me a training plan →" action (goal path
   * only — never a tune-up). Picking it calls this with the resolved race
   * (carrying the just-created `customerEventId` for customers, or by-value for
   * anon) and `setupEvent` returns `build_plan` WITHOUT creating a journey — the
   * wizard preloads the race and owns plan setup from there.
   */
  onBuildPlan?: (race: FunnelRace & { customerEventId?: string }) => void;
};

/**
 * The truthful three-way result of a race-journey create (robustness spec §5
 * Phase 1, fixes B1): a `nest_new` resolution SUCCEEDS but produces no journey,
 * which used to collapse into the same `null` as a cancelled sheet — callers
 * read the success as a cancel, kept the wizard open, and the user looped on
 * the same 409 forever. `nestedIntoName` names the build the race nested into
 * (from the 409 conflict payload) so callers can toast honestly.
 */
export type RaceJourneyOutcome =
  | { kind: "journey"; created: CreateJourneyResult }
  | {
      kind: "milestone";
      /** The raw `{id, intent}` milestone-create event (no name — see
       *  `CreateMilestoneResult`); null for anon principals. */
      event: CreateMilestoneResult["event"];
      nestedIntoName: string | null;
    }
  /**
   * The user took the sheet's "Just save the event for now" exit (robustness
   * spec §3 intent 3, funnel entries only — offered via
   * `opts.offerEventOnly`). Distinct from "cancelled": it's a CHOSEN outcome,
   * not a dismissal, so callers can frame it without the "unresolved" hint
   * (and G4 can diverge the two later without another wire change).
   */
  | { kind: "event_only" }
  | { kind: "cancelled" };

export type UseEventSetupResult = {
  /** The one funnel — see module doc. */
  setupEvent: (input: EventSetupInput) => Promise<EventSetupOutcome>;
  /**
   * Journey create with overlap handling: on `409 journey_overlap` opens the
   * unified `RoleResolutionSheet` and retries the create with the picked
   * resolution (`overlapResolution.mode` for chain/nest_existing, or
   * `resolveAsMilestone` for `nest_new`). The outcome is a THREE-way union —
   * "milestone" (the `nest_new` retry succeeded; the race is now a tune-up, no
   * journey exists) is a SUCCESS and must never be treated as "cancelled".
   * All journey creates go through here.
   */
  createRaceJourney: (
    body: CreateJourneyBody,
    raceNameForSheet?: string | null,
    /**
     * `offerEventOnly` (spec §3 intent 3): render a "Just save the event for
     * now" tertiary exit on the overlap sheet, resolving `{kind:"event_only"}`.
     * Only truthful when the caller's event row already exists (or is kept) —
     * the funnel's step 5. The wizard apply must NOT pass it (a plan apply
     * with no journey is Phase-3 scope).
     */
    opts?: { offerEventOnly?: boolean }
  ) => Promise<RaceJourneyOutcome>;
  /**
   * The funnel's no-event branch (P3, D5): create the ONE rolling training
   * journey (anchor = today server-side). `opts.dropWeeks` sets the cadence
   * (default 2 wks) and `opts.coverageWeeks` how many weeks to tile forward
   * initially (default: the server's fixed 3-window horizon) — both collected
   * by the wizard's no-event rolling on-ramp. The server archive-and-replaces
   * any existing rolling journey; callers that only want to ensure one exists should
   * check `journeys` first. Never 409s (rolling windows pause under races
   * instead of colliding).
   */
  createRollingJourney: (opts?: {
    dropWeeks?: number;
    coverageWeeks?: number;
  }) => Promise<CreateJourneyResult>;
  /**
   * The wizard-apply journey create (called by `useWizardFinish` for every
   * wizard host, so there is ONE funnel): profile-first PATCH, journey
   * create (with the step-2 window + overlap handling), anon pending-race
   * draft. Returns the same three-way `RaceJourneyOutcome` as
   * `createRaceJourney` — "milestone" is a success (toast + close), only
   * "cancelled" keeps the wizard open. Callers own their toasts/banners/refreshes.
   */
  applyWizardPlan: (body: WizardApplyBody) => Promise<RaceJourneyOutcome>;
};

/** Build the `pair` sheet context for a client-side pre-check collision: the
 *  new (as-yet-uncreated) race vs the containing active journey. The new event
 *  has no journey yet (`hasJourney:false`); the containing one always does. */
function pairContextFromContaining(
  race: FunnelRace,
  newStartYmd: string,
  containing: JourneyView
): RoleResolutionContext | null {
  const exRaceYmd = containing.raceYmd;
  const exStartYmd = containing.planStartYmd ?? containing.drops[0]?.startYmd ?? exRaceYmd;
  if (!exRaceYmd || !exStartYmd) return null;
  const newEvent: EventRef = {
    id: race.customerEventId ?? "new-event",
    name: race.name,
    ymd: race.dateYmd,
    hasJourney: false
  };
  const existingEvent: EventRef = {
    id: containing.customerEventId ?? containing.id,
    name: journeyRaceName(containing),
    ymd: exRaceYmd,
    hasJourney: true
  };
  const result = evaluateCollision(newEvent, existingEvent, {
    newStartYmd,
    newRaceYmd: race.dateYmd,
    exStartYmd,
    exRaceYmd
  });
  if (!result.collides) return null;
  const anchor = result.anchorId === newEvent.id ? newEvent : existingEvent;
  return { kind: "pair", events: [newEvent, existingEvent], anchor, outcomes: result.outcomes };
}

/** Build the `pair` sheet context from a server `journey_overlap` 409. The new
 *  event may not have an id yet (anon / not-yet-created); we fall back to a
 *  sentinel so the sheet always has stable keys. */
function pairContextFromConflict(
  conflict: JourneyOverlapConflict,
  newRaceName: string | null | undefined,
  newRaceYmd: string,
  /** §6.3 multi-conflict counter for the sheet eyebrow ("1 of N"). `index` is
   *  1-based (rounds so far incl. this one); `count` the known total. Omitted
   *  (or count 1) renders the single-conflict sheet unchanged. */
  counter?: { index: number; count: number }
): RoleResolutionContext {
  const newEvent: EventRef = {
    id: conflict.newEvent?.id ?? "new-event",
    name: conflict.newEvent?.name ?? newRaceName?.trim() ?? "your new race",
    ymd: conflict.newEvent?.ymd ?? newRaceYmd,
    hasJourney: false
  };
  const existingEvent: EventRef = {
    id: conflict.existing.journeyId,
    name: conflict.existing.raceName ?? "your current race",
    ymd: conflict.existing.raceYmd ?? "",
    hasJourney: conflict.existing.hasJourney ?? true
  };
  // The server-supplied `resolutions` are authoritative; the anchor is the later
  // race (the new one when it is later, else the existing).
  const newIsLater =
    !!existingEvent.ymd && compareYmd(newEvent.ymd, existingEvent.ymd) > 0;
  const anchor = newIsLater ? newEvent : existingEvent;
  return {
    kind: "pair",
    events: [newEvent, existingEvent],
    anchor,
    outcomes: conflict.resolutions,
    conflictIndex: counter?.index,
    conflictCount: counter?.count,
    // G5: the server's consequence preview for THIS conflict rides into the
    // sheet so cards can say what each pick removes/moves. Absent (old
    // server) → the cards render without consequence lines, as before.
    consequences: conflict.consequences ?? undefined
  };
}

/** Map a `nest_existing` outcome to its wire mode; `chain` passes through. */
function outcomeToOverlapMode(outcome: RoleOutcome): OverlapMode | null {
  if (outcome === "nest_existing") return "demote";
  if (outcome === "chain") return "chain";
  return null; // nest_new is a create-time outcome, not an overlap mode
}

export function useEventSetup(): UseEventSetupResult {
  const { state: authState, refreshCsrf } = useAuth();
  const { open: openModal, close: closeModal } = useModal();
  const isCustomer = authState.kind === "customer";
  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon" ? authState.csrfToken : null;
  // These callbacks get frozen into modal nodes at open time (the wizard's
  // `onApply`, the funnel prompts). Reading CSRF from a ref — refreshed every
  // render — means the token is resolved at CALL time, so a callback captured
  // during the brief auth-loading window (e.g. an instant landing-door click on
  // a cold load) no longer fails the apply with a stale null token.
  const csrfRef = useRef(csrfToken);
  csrfRef.current = csrfToken;

  /**
   * The ONE role-decision prompt (spec §9.1, C2). Opens `RoleResolutionSheet`
   * with the geometry-derived `pair` outcomes and resolves to the picked
   * `RoleResolutionChoice`, the `{kind:"event_only"}` sentinel (the "Just save
   * the event for now" exit, only rendered when `opts.offerEventOnly` — spec
   * §3 intent 3), or null when dismissed. Replaces the old `promptIntent` +
   * `promptOverlapResolution`.
   */
  const promptRoleResolution = useCallback(
    (
      context: RoleResolutionContext,
      opts?: {
        offerEventOnly?: boolean;
        offerRemoveExisting?: boolean;
        /** Part 2 remove-race choice: also offer "Remove {race} entirely"
         *  (delete the event + archive its journey). Only passed when the 409's
         *  `existing.customerEventId` is present AND the principal is a
         *  customer (the DELETE route is customer-only). */
        offerRemoveEntirely?: boolean;
        /** §6.1 hatch (spec §3 intent 2): the conflicting EXISTING event is a
         *  CUSTOM one, so the sheet may offer "Edit {name}'s date instead".
         *  Resolves the distinct `{kind:"edit_date"}` sentinel — the caller
         *  (the 409 loop) opens `EventDateEditModal` and continues/cancels. */
        offerEditDate?: { name: string };
      }
    ) =>
      new Promise<
        RoleResolutionChoice | { kind: "event_only" } | { kind: "edit_date" } | null
      >((resolve) => {
        // Settle-once guard: `closeModal(id)` synchronously fires the stack
        // entry's `onClose`, so we must record the outcome BEFORE closing (else
        // the scrim `onClose` → null would clobber a real pick). Wiring
        // `onClose` here is what makes a scrim-click / Escape dismiss resolve
        // as cancelled instead of leaving the awaiting funnel hung. The
        // event-only exit settles through the SAME funnel, so it can never
        // race a card pick or a dismiss.
        let settled = false;
        const finish = (
          choice: RoleResolutionChoice | { kind: "event_only" } | { kind: "edit_date" } | null
        ) => {
          if (settled) return;
          settled = true;
          resolve(choice);
        };
        const id = openModal(
          createElement(RoleResolutionSheet, {
            context,
            // B3: the sheet renders its own "Remove {existing} instead"
            // two-step action; the pick arrives as the ordinary
            // `remove_existing` pair choice through `onResolve`.
            offerRemoveExisting: opts?.offerRemoveExisting,
            offerRemoveEntirely: opts?.offerRemoveEntirely,
            // This funnel only ever opens a `pair` sheet → a 1-element batch.
            onResolve: (choices: RoleResolutionChoice[]) => {
              finish(choices[0] ?? null);
              closeModal(id);
            },
            onCancel: () => {
              finish(null);
              closeModal(id);
            },
            // Escape-hatch slot (B1/§6.1): the funnel-only "save the event,
            // decide the plan later" tertiary exit, plus the date-edit hatch
            // when the conflicting existing event is CUSTOM. `id` is safely
            // referenced — the onClick closures run long after openModal
            // returns. A single hatch is passed as the bare button (kept for
            // callers/tests that read `extraActions.props` directly); two
            // become a keyed array in the same extra-actions region.
            extraActions: (() => {
              const extras = [];
              if (opts?.offerEventOnly) {
                extras.push(
                  createElement(
                    "button",
                    {
                      key: "rr-event-only",
                      type: "button",
                      className: "fg-btn fg-btn--ghost fg-btn--sm",
                      "data-testid": "rr-event-only",
                      onClick: () => {
                        finish({ kind: "event_only" });
                        closeModal(id);
                      }
                    },
                    "Just save the event for now"
                  )
                );
              }
              if (opts?.offerEditDate) {
                extras.push(
                  createElement(
                    "button",
                    {
                      key: "rr-edit-date",
                      type: "button",
                      className: "fg-btn fg-btn--ghost fg-btn--sm",
                      "data-testid": "rr-edit-date",
                      onClick: () => {
                        finish({ kind: "edit_date" });
                        closeModal(id);
                      }
                    },
                    `Edit ${opts.offerEditDate.name}'s date instead`
                  )
                );
              }
              if (extras.length === 0) return undefined;
              return extras.length === 1 ? extras[0] : extras;
            })()
          }),
          { onClose: () => finish(null) }
        );
      }),
    [openModal, closeModal]
  );

  /**
   * §6.1 hatch follow-through: open the shared `EventDateEditModal` for the
   * conflicting EXISTING custom event and await its outcome. The modal owns
   * its whole flow (PATCH + its own nested collision loop + success toast +
   * `notifyJourneyChanged`) — this wrapper only bridges it into the awaiting
   * 409 loop: `onSaved` → "saved" (the loop re-attempts the create),
   * `onClose` without a save → "closed" (the loop treats it as a sheet
   * cancel). Settle-once: the modal fires `onSaved` THEN `onClose` on
   * success, so "saved" must be recorded first.
   */
  const promptDateEdit = useCallback(
    (event: { id: string; name: string; dateYmd: string }) =>
      new Promise<"saved" | "closed">((resolve) => {
        let settled = false;
        const finish = (outcome: "saved" | "closed") => {
          if (settled) return;
          settled = true;
          resolve(outcome);
        };
        const id = openModal(
          createElement(EventDateEditModal, {
            // The hatch only renders for CUSTOM events (eventCatalogId null).
            event: { ...event, eventCatalogId: null },
            onSaved: () => finish("saved"),
            onClose: () => {
              finish("closed");
              closeModal(id);
            }
          }),
          { onClose: () => finish("closed") }
        );
      }),
    [openModal, closeModal]
  );

  /**
   * The mini window/cadence confirm sheet (J2/D2). Resolves the DISTINCT sheet
   * outcomes (C8): "Create race plan →" → the picked `RacePlanWindowChoice`;
   * "Build me a training plan →" → the `{ kind: "build_plan" }` sentinel;
   * "Just save the event" AND the X/close → `null` (→ `event_only`). The
   * `onBuildPlan` action is only rendered when `offerBuildPlan` is true (the
   * caller passed `input.onBuildPlan` AND intent resolved to `goal`).
   */
  const promptWindow = useCallback(
    (
      race: FunnelRace,
      libraryLengths?: number[] | null,
      offerBuildPlan = false,
      impact?: RacePlanImpact | null
    ) =>
      new Promise<RacePlanWindowChoice | { kind: "build_plan" } | null>((resolve) => {
        // Settle-once guard (see promptRoleResolution): record the outcome
        // before `closeModal` fires the entry `onClose`, and wire that `onClose`
        // so a scrim / Escape dismiss resolves as null instead of hanging.
        let settled = false;
        const finish = (choice: RacePlanWindowChoice | { kind: "build_plan" } | null) => {
          if (settled) return;
          settled = true;
          resolve(choice);
        };
        const id = openModal(
          createElement(RacePlanConfirmModal, {
            eventName: race.name,
            raceYmd: race.dateYmd,
            libraryLengths,
            impact,
            onConfirm: (choice: RacePlanWindowChoice) => {
              finish(choice);
              closeModal(id);
            },
            onSkip: () => {
              finish(null);
              closeModal(id);
            },
            // Only wire the tertiary opt-in when the caller offered it — its
            // absence keeps the sheet's plain two-button layout.
            onBuildPlan: offerBuildPlan
              ? () => {
                  finish({ kind: "build_plan" });
                  closeModal(id);
                }
              : undefined,
            onClose: () => {
              finish(null);
              closeModal(id);
            }
          }),
          { onClose: () => finish(null) }
        );
      }),
    [openModal, closeModal]
  );

  const createRaceJourney = useCallback<UseEventSetupResult["createRaceJourney"]>(
    async (body, raceNameForSheet, opts) => {
      const csrf = csrfRef.current;
      if (!csrf) throw new Error("Couldn't create the race plan — refresh and try again.");
      // §6.3 accumulate-and-retry: the server re-runs its overlap gate after
      // applying the supplied resolutions, so ONE create can answer several
      // successive 409s (one per still-colliding journey). Each sheet round
      // pushes its pick here and the retry carries the WHOLE array — nothing is
      // ever partially committed, because resolutions only ride the create
      // request (a cancel at any round abandons them all). Any caller-supplied
      // singular `overlapResolution` (wizard pre-check, B2) stays on `body`
      // untouched — the server treats it as targeting the first conflict.
      const resolved: Array<{ journeyId: string; mode: OverlapMode }> = [];
      // Defensive cap on SHEET ROUNDS — an unbounded 409 loop is the exact
      // failure mode Phase 1 (B1) fixed; never reintroduce it. Counted as
      // ROUNDS (not `resolved.length`): a `remove_existing` pick accumulates
      // no resolution, so counting picks would let a pathological 409 stream
      // of removals loop forever.
      const MAX_SHEET_ROUNDS = 6;
      let rounds = 0;
      let attemptBody: CreateJourneyBody = body;
      for (;;) {
        let conflict: JourneyOverlapConflict;
        try {
          const created = await createJourney(attemptBody, csrf, refreshCsrf);
          return { kind: "journey", created };
        } catch (err) {
          const parsed = journeyOverlapConflictFromError(err);
          if (!parsed) throw err;
          conflict = parsed;
        }
        rounds += 1;
        if (rounds > MAX_SHEET_ROUNDS) {
          throw new Error("We couldn't resolve the race overlaps — please try again.");
        }
        const context = pairContextFromConflict(
          conflict,
          raceNameForSheet ?? body.raceName,
          body.raceDate ?? "",
          {
            // 1-based round number; the server's `conflictCount` is the count
            // REMAINING at this 409, so the known total = rounds already run +
            // remaining. Absent count → 1 → the sheet renders no counter.
            index: rounds,
            count: rounds - 1 + (conflict.conflictCount ?? 1)
          }
        );
        // B3: the server 409 names the conflicting journey, so this loop can
        // truthfully offer "Remove {existing race} instead" (the client
        // pre-check sheet cannot — it never passes the flag).
        // §6.1 (spec §3 intent 2): when the conflict's existing event is
        // CUSTOM (customerEventId present, eventCatalogId null) the sheet also
        // offers "Edit {name}'s date instead" — moving the OLD race usually
        // clears the collision entirely.
        const existingCustomEventId =
          conflict.existing.customerEventId && conflict.existing.eventCatalogId == null
            ? conflict.existing.customerEventId
            : null;
        // Part 2 remove-race choice: the "Remove {race} entirely" action needs
        // the conflict's event id (stitch lane) AND a customer principal — the
        // DELETE /api/events/:id route is customer-only.
        const existingEventId = conflict.existing.customerEventId ?? null;
        const choice = await promptRoleResolution(context, {
          ...opts,
          offerRemoveExisting: true,
          offerRemoveEntirely: isCustomer && !!existingEventId,
          offerEditDate: existingCustomEventId
            ? { name: conflict.existing.raceName ?? "your current race" }
            : undefined
        });
        // "Just save the event for now" — a CHOSEN exit (spec §3 intent 3),
        // not a dismissal: no journey, all accumulated resolutions abandoned
        // (they only ever ride the create request), the caller's event stays.
        if (choice?.kind === "event_only") return { kind: "event_only" };
        // §6.1 hatch: open the date editor for the EXISTING event. Saved →
        // continue the retry loop (the round already counted against the
        // sheet-round cap; a still-colliding date simply 409s again). Closed
        // without saving → the same abandonment as a sheet cancel.
        if (choice?.kind === "edit_date") {
          const edited = await promptDateEdit({
            id: existingCustomEventId as string,
            name: conflict.existing.raceName ?? "your current race",
            dateYmd: conflict.existing.raceYmd ?? ""
          });
          if (edited === "saved") continue;
          return { kind: "cancelled" };
        }
        if (!choice || choice.kind !== "pair") return { kind: "cancelled" };

        // remove_existing (spec §3 intent 1, B3): archive the OLD race's build
        // NOW via the same primitive as the Journey-settings "Stop this race
        // plan…" chooser (`{ status: "archived" }` PATCH — anon-capable; the
        // event row survives as a saved event, and paid boxes stay in history),
        // then retry the create. CLIENT-ONLY: never sent as an overlap mode,
        // and nothing is pushed into `resolved` — the conflicting journey is
        // gone, so the gate simply stops naming it. Other conflicts may still
        // 409 → next sheet round.
        if (choice.outcome === "remove_existing") {
          try {
            try {
              await patchJourney(
                conflict.existing.journeyId,
                { status: "archived" },
                csrf,
                refreshCsrf
              );
            } catch (err) {
              // Archive backstop (spec §9.3): a goal build with nested
              // tune-ups 409s `goal_archive_tuneups` — retry with the
              // chooser's safe default (each tune-up reverts to its own goal
              // event; nothing else is deleted).
              if (goalArchiveTuneupsFromError(err)) {
                await patchJourney(
                  conflict.existing.journeyId,
                  { status: "archived", tuneupDisposition: "keep_as_goal" },
                  csrf,
                  refreshCsrf
                );
              } else {
                throw err;
              }
            }
          } catch (err) {
            const name = conflict.existing.raceName ?? "that race";
            throw new Error(
              userFacingError(err, `Couldn't remove ${name} — please try again.`)
            );
          }
          notifyJourneyChanged();
          continue;
        }

        // remove_existing_event (Part 2 remove-race choice): delete the OLD
        // race's event AND archive its journey in one server transaction via
        // the sanctioned DELETE route (`disposition=archive` — the same
        // semantics as the Journey-settings chooser's "remove the race": the
        // journey archives, paid boxes stay in history; nothing hard-deletes).
        // `tuneupDisposition=keep_as_goal` rides along as the safe default so
        // a goal with nested tune-ups doesn't 409 — each tune-up reverts to
        // its own goal event. CLIENT-ONLY: nothing is pushed into `resolved` —
        // the conflicting journey (and its event) are gone, so the gate simply
        // stops naming them. Other conflicts may still 409 → next sheet round.
        if (choice.outcome === "remove_existing_event") {
          const name = conflict.existing.raceName ?? "that race";
          // Gated above (`offerRemoveEntirely` requires the id), so a missing
          // id here is a defensive impossibility — treat as a cancel.
          if (!existingEventId) return { kind: "cancelled" };
          try {
            await deleteEvent(existingEventId, csrf, refreshCsrf, {
              disposition: "archive",
              tuneupDisposition: "keep_as_goal"
            });
          } catch (err) {
            throw new Error(
              userFacingError(err, `Couldn't remove ${name} — please try again.`)
            );
          }
          notifyJourneyChanged();
          continue;
        }

        // nest_new: the NEW (earlier) race becomes a tune-up — retry the create
        // with `resolveAsMilestone` so the server stamps the event a milestone
        // and creates NO journey. That is a SUCCESS with no journey — return the
        // distinct "milestone" outcome (B1: this used to collapse into the same
        // null as a cancel, so the wizard looped on the 409 forever). A
        // milestone has no window, so it short-circuits any remaining conflicts.
        if (choice.outcome === "nest_new") {
          const res = await createJourneyOrMilestone(
            { ...body, resolveAsMilestone: true },
            csrf,
            refreshCsrf
          );
          // A journey result (server chose to build anyway) proceeds as a goal.
          if (!isMilestoneResult(res)) return { kind: "journey", created: res };
          notifyJourneyChanged();
          return {
            kind: "milestone",
            event: res.event,
            nestedIntoName: conflict.existing.raceName ?? null
          };
        }

        const mode = outcomeToOverlapMode(choice.outcome);
        if (!mode) return { kind: "cancelled" };
        // Each pick names the journey it resolves (nest_existing → "demote") so
        // a stale entry can never archive the wrong build server-side.
        resolved.push({ journeyId: conflict.existing.journeyId, mode });
        attemptBody = { ...body, overlapResolutions: [...resolved] };
      }
    },
    [refreshCsrf, promptRoleResolution, promptDateEdit, isCustomer]
  );

  const createRollingJourney = useCallback<UseEventSetupResult["createRollingJourney"]>(
    async (opts) => {
      const csrf = csrfRef.current;
      if (!csrf) {
        throw new Error("Couldn't start your training journey — refresh and try again.");
      }
      const created = await createJourney(
        {
          method: "build",
          journeyType: "rolling",
          // Cadence + coverage from the no-event setup sheet (omitted ⇒ server
          // defaults: 2-week cadence, fixed 3-window horizon). Stripped safely
          // by the schema on a pre-coverage server.
          dropWeeks: opts?.dropWeeks,
          coverageWeeks: opts?.coverageWeeks
        },
        csrf,
        refreshCsrf
      );
      notifyJourneyChanged();
      return created;
    },
    [refreshCsrf]
  );

  const setupEvent = useCallback<UseEventSetupResult["setupEvent"]>(
    async (input) => {
      const { race, journeys, createEvent, libraryLengths } = input;
      const today = localTodayYmd();

      // 0a · Guard an empty/invalid race date. `catalogEventStartToYmd` can
      // return "" for an unparseable catalog `startDate`, and "" sorts BEFORE
      // any real YMD — so without this guard it would silently fall into the
      // past-branch below and return `event_only` (dropping the race). Surface
      // an error instead of guessing "past".
      if (!/^\d{4}-\d{2}-\d{2}$/.test(race.dateYmd)) {
        throw new Error("This event is missing a valid date — pick a date and try again.");
      }

      // 0b · Past-dated events never grow a journey — save the row and stop
      // (unchanged pre-funnel behavior for historical entries).
      if (compareYmd(race.dateYmd, today) < 0) {
        const event = createEvent ? await createEvent("goal") : null;
        return { outcome: "event_only", event };
      }

      // The default window start for the new race (drives the collision pre-check
      // and, when no window is chosen later, the journey create).
      const defaultWindow = defaultJourneyWindow({
        raceYmd: race.dateYmd,
        todayYmd: today,
        libraryLengths
      });

      // 1 · Role resolution (J4/§9.1): the goal-vs-tune-up decision is only asked
      // when the picked race collides with an active journey's window. Open-time
      // picks default to goal with no question. `forcedIntent` short-circuits
      // (wizard "Add as tune-up" paths).
      let intent: EventIntent;
      if (input.forcedIntent) {
        intent = input.forcedIntent;
      } else {
        const containing = journeyCoveringDate(journeys, race.dateYmd);
        if (containing) {
          const context = pairContextFromContaining(race, defaultWindow.startYmd, containing);
          if (context) {
            const choice = await promptRoleResolution(context);
            if (!choice || choice.kind !== "pair") return { outcome: "cancelled" };
            // nest_new → the new race is a tune-up (milestone); every other
            // outcome (chain/nest_existing) keeps it as a goal — the
            // window/create steps below apply the chosen overlap resolution via
            // the server 409 retry (createRaceJourney).
            intent = choice.outcome === "nest_new" ? "milestone" : "goal";
          } else {
            // Containment without a resolvable geometry window — keep as goal.
            intent = "goal";
          }
        } else {
          intent = "goal";
        }
      }

      // 2 · Event create (customer) — with the resolved intent. Entry points
      // pass no `createEvent` for a guest: an anon race becomes a real
      // `CustomerEvent` inside the journey create below (step 5), not here.
      let event: EventDTO | null = null;
      if (createEvent) event = await createEvent(intent);
      const customerEventId = race.customerEventId ?? event?.id;

      // 3 · Milestone: no journey — the tune-up rides its containing build
      // (its own race-day kit generation is a later phase, D1/P5).
      //
      // DRAFT STILL LOAD-BEARING for a guest: this branch returns before any
      // journey create, so nothing server-side exists — the race would be lost
      // without the pending draft the login replay recreates.
      if (intent === "milestone") {
        if (!isCustomer) {
          addAnonPendingRaceEvent({
            name: race.name,
            dateYmd: race.dateYmd,
            eventCatalogId: race.eventCatalogId,
            distanceLabel: race.distanceLabel,
            intent: "milestone"
          });
        }
        return { outcome: "milestone", event };
      }

      // 4 · Window (J2/D2): caller-confirmed, sheet-confirmed, or defaulted.
      // Reaching here means intent === "goal" (the milestone branch returned at
      // step 3), so the "Build me a training plan" opt-in is offered whenever the
      // caller wired `input.onBuildPlan` — never for a tune-up (it rides an
      // existing build, so it has no plan of its own).
      let window = input.window ?? null;
      if (!window && input.askWindow) {
        // 5b — real-data impact for the sheet's caption block, computed from the
        // caller's active journeys across the DEFAULT window (the start the sheet
        // opens on). Additive apply, so this reassures rather than warns.
        const impact = computeRacePlanImpact(journeys, {
          startYmd: defaultWindow.startYmd,
          raceYmd: race.dateYmd
        });
        const choice = await promptWindow(race, libraryLengths, !!input.onBuildPlan, impact);
        if (choice && "kind" in choice) {
          // "Build me a training plan →": hand the race (with its just-created
          // customer event id, if any) to the wizard and STOP — we deliberately
          // create no journey here (the wizard owns plan setup). Anon has no
          // event yet, so `customerEventId` is undefined and the race travels by
          // value; the wizard's own apply creates the durable anon event.
          input.onBuildPlan?.({ ...race, customerEventId });
          return { outcome: "build_plan", event };
        }
        // `choice` is now a `RacePlanWindowChoice` ("Create race plan →") or
        // `null` ("Just save the event" / X-close).
        window = choice;
        if (!window) {
          // "Just save the event" — no journey now, so for an anon principal
          // NOTHING was written server-side (the funnel's `createEvent` is
          // customer-only): the draft is still load-bearing here, exactly as in
          // the milestone branch. Without it a guest who skips the window sheet
          // loses the race entirely.
          if (!isCustomer) {
            addAnonPendingRaceEvent({
              name: race.name,
              dateYmd: race.dateYmd,
              eventCatalogId: race.eventCatalogId,
              distanceLabel: race.distanceLabel,
              intent: "goal"
            });
          }
          return { outcome: "event_only", event };
        }
      }
      let windowIsSilentDefault = false;
      if (!window) {
        windowIsSilentDefault = true;
        window = { startYmd: defaultWindow.startYmd, weeks: defaultWindow.weeks, dropWeeks: 2 };
      }

      // 5 · Journey create (method "build" — no training plan; the user's own
      // workouts fill the window). Overlap 409 → sheet → retry.
      //
      // Anchor P6: the SILENT default window is the only start the user never
      // saw. When the runway cap pins it to today, omit startYmd so the server
      // anchors at the next fuel-eligible workout (capped at race day) instead
      // of the create moment. Sheet-confirmed and caller-passed windows were
      // shown to the user and stay explicit.
      const sendStartYmd =
        windowIsSilentDefault && window.startYmd === today ? undefined : window.startYmd;
      const outcome = await createRaceJourney(
        {
          method: "build",
          raceDate: race.dateYmd,
          raceName: race.name,
          eventCatalogId: race.eventCatalogId,
          distanceLabel: race.distanceLabel,
          customerEventId,
          dropWeeks: window.dropWeeks,
          startYmd: sendStartYmd,
          journeyType: "race"
        },
        race.name,
        // E3/E4: the event row exists (or was kept) by this point, so the
        // sheet may truthfully offer "Just save the event for now" (§3
        // intent 3). The wizard-apply path deliberately does NOT.
        { offerEventOnly: true }
      );
      // Sheet resolved to `nest_new` → the race IS saved, as a tune-up riding
      // the existing build (truthful "milestone", not "event_only" — B1/§5.4).
      //
      // DRAFT STILL LOAD-BEARING for a guest: the server's `resolveNewAsMilestone`
      // creates NO event for an anon principal (it returns `{milestone, event:null}`
      // and creates no journey either), so the race exists only in this draft
      // until the login replay recreates it.
      if (outcome.kind === "milestone") {
        if (!isCustomer) {
          addAnonPendingRaceEvent({
            name: race.name,
            dateYmd: race.dateYmd,
            eventCatalogId: race.eventCatalogId,
            distanceLabel: race.distanceLabel,
            intent: "milestone"
          });
        }
        return { outcome: "milestone", event };
      }
      // "Just save the event for now": the same `event_only` return as a
      // cancel below — but a CHOSEN exit, not a dismissal. The distinct
      // `RaceJourneyOutcome` kind future-proofs G4 (per-entry cancel
      // semantics) without changing behavior beyond the new button.
      if (outcome.kind === "event_only") return { outcome: "event_only", event };
      // Sheet cancelled: the event row (if created above) stays — event only.
      if (outcome.kind === "cancelled") return { outcome: "event_only", event };
      const created = outcome.created;

      // 6 · No anon draft here (customer-event-anon-ownable). The build create
      // above carried `raceDate`, so the server made a DURABLE anon-owned
      // `CustomerEvent` and linked it to the journey — it comes back on
      // `created.journey.customerEventId`, names the journey server-side
      // (`resolveRaceHeaderName` reads the link for any principal) and is
      // re-pointed onto the account by the login merge. A localStorage draft
      // would only be a weaker duplicate of that row.

      notifyJourneyChanged();
      return { outcome: "goal", journey: created.journey, event };
    },
    [isCustomer, promptRoleResolution, promptWindow, createRaceJourney]
  );

  const applyWizardPlan = useCallback<UseEventSetupResult["applyWizardPlan"]>(
    async (body) => {
      const csrf = csrfRef.current;
      if (!csrf) throw new Error("Couldn't apply the plan — refresh and try again.");
      // PROFILE-FIRST (fueling-profile plan R4): athlete-state persists before
      // the journey create — it's user-level data, so keeping it on a failed
      // create is correct, and the retry re-PATCHes identical values (no-op).
      const wroteProfile = !!body.fuelingProfile && Object.keys(body.fuelingProfile).length > 0;
      if (body.fuelingProfile && wroteProfile) {
        await patchFuelingProfile(body.fuelingProfile, csrf, refreshCsrf);
      }
      let outcome: RaceJourneyOutcome;
      try {
        outcome = await createRaceJourney(
          {
            // No planId → a plan-less "build your own" race journey.
            method: body.method ?? (body.planId ? "fuel_goods_plan" : "build"),
            planId: body.planId,
            raceDate: body.raceDate,
            raceName: body.raceName,
            eventCatalogId: body.eventCatalogId,
            distanceLabel: body.distanceLabel,
            customerEventId: body.customerEventId,
            dropWeeks: body.dropWeeks,
            customTargets: body.customTargets,
            // J2 window start from the wizard's step-2 selection (server
            // stores it from P1/P2; stripped harmlessly until then).
            startYmd: body.startYmd,
            // B2 (double-ask): the wizard's PRE-CHECK sheet answer (chain/
            // demote) rides the create so the server resolves the
            // overlap without re-opening the same question on a 409.
            overlapResolution: body.overlapResolution,
            journeyType: "race"
          },
          body.raceName
        );
      } catch (err) {
        // Keep the server's real reason visible — don't bury a validation or
        // conflict message. When the profile already persisted, add the R4
        // reassurance that a retry is safe rather than replacing the cause.
        if (!wroteProfile) throw err;
        const reason = userFacingError(err, "");
        throw new Error(
          reason
            ? `${reason} Your fueling profile is saved — just retry to finish creating your race plan.`
            : "Your fueling profile is saved — retry to finish creating your race plan."
        );
      }
      if (outcome.kind === "cancelled") return outcome; // sheet dismissed — caller keeps the wizard open
      // The wizard apply never offers the event-only exit (no opts passed
      // above — a plan apply with no journey is Phase-3 scope), so this arm is
      // unreachable today; kept for exhaustive narrowing.
      if (outcome.kind === "event_only") return outcome;
      // `nest_new` resolution: the race is saved as a tune-up (no journey, no
      // plan). A SUCCESS — the caller toasts + closes.
      //
      // DRAFT STILL LOAD-BEARING for a guest: `resolveNewAsMilestone` returns
      // `event: null` for an anon principal and applies no plan, so this draft
      // is the only record until the login replay recreates it with the
      // milestone intent (B1 sibling: it used to be skipped, losing the race).
      if (outcome.kind === "milestone") {
        if (!isCustomer) {
          addAnonPendingRaceEvent({
            name: body.raceName?.trim() || "Goal race",
            dateYmd: body.raceDate,
            eventCatalogId: body.eventCatalogId,
            distanceLabel: body.distanceLabel,
            intent: "milestone"
          });
        }
        return outcome;
      }
      notifyJourneyChanged();
      // No anon draft here (customer-event-anon-ownable). Both create methods
      // give a guest a DURABLE anon-owned `CustomerEvent`: `fuel_goods_plan`
      // creates it inside `applyTrainingPlan`'s transaction and stamps every
      // applied workout with its id (returned as `created.applied.event`);
      // plan-less `build` creates it from `raceDate` on the journey. Either way
      // the row is linked to the journey and re-pointed onto the account by the
      // login merge — the draft would only duplicate it.
      return outcome;
    },
    [isCustomer, refreshCsrf, createRaceJourney]
  );

  return { setupEvent, createRaceJourney, createRollingJourney, applyWizardPlan };
}
